import { Component, OnInit, Input, AfterViewInit } from '@angular/core';
import { AuthConfigService, StoreService, TranslationService } from 'projects/dop-core/src/public-api';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { LabDetails } from '../models/lab-details.model';
import { AdditionalField } from '../models/additional-field.model';
import { AdditionalFieldHelper } from '../services/additional-field-helper.service';
import { CurrentOrder } from '../models/current-order.model';
import { CurrentOrderService } from '../services/current-order.service';
import { OrderStatus } from '../models/my-orders/order-status.model';
import { Address } from 'src/app/shared/models/address';
import { CountryAddressFormatRequest } from 'src/app/shared/models/country-address-format-request.model';
import { AddShippingService } from 'src/app/features/guided-order/order-n-ship/add-shipping-method/add-shipping-method.service';
import { EnableAttachment } from '../enums/enable-attachment.enum';
import { AttachDocument } from '../models/attach-document';
import { ScanStatus } from '../enums/scan-status.enum';
import { DownloadFileHelperService } from '../services/download-file-helper.service';
import { AttachDocumentHelperService } from '../services/attach-document-helper.service';
import { FileStatus } from '../enums/file-status.enum';
import { StringFormatter } from '../pipes/string-formatter.pipe';
import { BrowserFinder } from '../extensions/browser.finder';
import { FormatterHelperService } from '../services/formatter-helper.service';
import { ScheduleOrder } from '../models/my-orders/schedule-order-details.model';
import { AccountLabFormattedAddress } from '../models/account-lab-formatted-address';

@Component({
  selector: 'app-order-detail-preview',
  templateUrl: './order-detail-preview.component.html',
  styleUrls: ['./order-detail-preview.component.scss']
})
export class OrderDetailPreviewComponent implements OnInit, AfterViewInit {

  @Input() orderDetails: CurrentOrder;
  @Input() scheduleOrderDetails?: ScheduleOrder;
  @Input() isViewSchedule : boolean = false;
  labDetails: LabDetails;
  additionalFields: AdditionalField[];
  accountFormattedAddress: string;
  labFormattedAddress: string;
  downloadUrl: string;
  isChrome = false;

  constructor(
    private storeService: StoreService,
    private additionalFieldHelper: AdditionalFieldHelper,
    private currentOrderService: CurrentOrderService,
    public addShippingService: AddShippingService,
    public attachDocumentHelperService: AttachDocumentHelperService,
    public downloadFileHelperService: DownloadFileHelperService,
    private authConfigService: AuthConfigService,
    private formatterHelperService: FormatterHelperService
  ) { }

  async ngOnInit(): Promise<void> {
    this.isChrome = BrowserFinder.isChrome();
    console.log('isViewSchedule = ',this.isViewSchedule);
    if (!this.orderDetails.Status) {
      this.orderDetails.Status = OrderStatus[OrderStatus.Pending]
      this.orderDetails.displayStatus = TranslationService.translate(OrderStatus.PendingState);
    }

    this.storeService.Get(StoreKey.LabFormattedAddress).toPromise().then((data: AccountLabFormattedAddress) => {
      if (data && data?.labCode === this.currentOrderService?.currentOrder?.ReceivingLabCode) {
        this.labFormattedAddress = data?.formattedAddress;
      } else {
        this.getFormattedAddress();
      }
    });

    this.storeService.Get(StoreKey.AccountFormattedAddress).toPromise().then((data: AccountLabFormattedAddress) => {
      if (data && data?.accountCode === this.currentOrderService?.currentOrder?.AccountCode) {
        this.accountFormattedAddress = data?.formattedAddress;
      }
    });

    if (this.orderDetails.Status === OrderStatus[OrderStatus.Pending]) {
      const shipCost = this.currentOrderService.getShippingPrice();
      const analysisCost = this.currentOrderService.getTotalPriceOfSamplesInOrder();
      const orderTotal = this.getGrandTotal(this.currentOrderService.getShippingPrice(),
        this.currentOrderService.getTotalPriceOfSamplesInOrder());
      const currencyCode = this.currentOrderService.currentOrder.quotation.currencyCode + ' ';
      this.orderDetails.OrderTotal = this.formatterHelperService.formatCurrency(orderTotal ?? 0, currencyCode);
      this.orderDetails.AnalysisCost = this.formatterHelperService.formatCurrency(analysisCost ?? 0, currencyCode);
      this.orderDetails.ShippingCost = this.formatterHelperService.formatCurrency(shipCost ?? 0, currencyCode);
      this.orderDetails.displayStatus = TranslationService.translate(OrderStatus.PendingState);
    }

    this.downloadUrl = this.authConfigService.authConfig.redirectUri + 'downloadFile?internalFileName=';
  }

  ngAfterViewInit(): void {
    this.currentOrderService.analysesRemovedEvent.subscribe(() =>{
      const analysisCost = this.currentOrderService.getTotalPriceOfSamplesInOrder();
      const orderTotal = this.getGrandTotal(this.currentOrderService.getShippingPrice(),
        this.currentOrderService.getTotalPriceOfSamplesInOrder());
      const currencyCode = this.currentOrderService.currentOrder.quotation.currencyCode + ' ';
      this.orderDetails.OrderTotal = this.formatterHelperService.formatCurrency(orderTotal ?? 0, currencyCode);
      this.orderDetails.AnalysisCost = this.formatterHelperService.formatCurrency(analysisCost ?? 0, currencyCode);
    }

    )
  }

  get attachments(): AttachDocument[] {
    return this.orderDetails.attachDocuments?.filter(x => x.status !== ScanStatus.Infected
      && x.fileStatus !== FileStatus.Archived && x.fileStatus !== FileStatus.ArchivedDeleted);
  }

  hasAttachments(): boolean {
    return this.orderDetails.attachDocuments?.filter(x => x.status !== ScanStatus.Infected).length > 0;
  }

  downloadAttachment(attachDocument: AttachDocument) {
    if (attachDocument.status === ScanStatus.Clean) {
      return this.downloadFileHelperService.downloadAttachment(attachDocument, () => { });
    }
  }

  noClean() {
    return this.orderDetails.attachDocuments?.filter(x => x.status === ScanStatus.Clean).length === 0;
  }

  clean(file: AttachDocument): boolean {
    return file.status === ScanStatus.Clean;
  }

  getArchivedFilesCount(): number {
    return this.currentOrderService.currentOrder.attachDocuments.filter(x => x.fileStatus === FileStatus.Archived
      || x.fileStatus === FileStatus.ArchivedDeleted)?.length;
  }

  getArchivedFilesMessage(): string {
    return StringFormatter.format(TranslationService.translate('vbeiDocumentsRemovedOver60DaysMessage'),
      this.getArchivedFilesCount().toString());
  }

  private async getFormattedAddress() {
    const addresses: Address[] = [];
    const request = new CountryAddressFormatRequest();

    addresses.push(this.mapAddress(this.orderDetails.labDetails));
    addresses.push(this.orderDetails.account.address);

    request.Addresses = addresses;
    const formattedAddresses = await this.addShippingService.getFormattedAddress(request);
    this.labFormattedAddress = formattedAddresses.data[0].replace(/\r\n/g, '<br>');
    this.accountFormattedAddress = formattedAddresses.data[1].replace(/\r\n/g, '<br>');
    var company = this.orderDetails.account.name != null ? this.orderDetails.account.name + '<br>' : "";
    var phone = this.orderDetails.account.phoneNumber != null ? this.orderDetails.account.phoneNumber : "";
    if(!phone.match(/^[0-9]{10}$/)){
      var phoneDigits = phone.replace(/[^0-9]/ig, "")
      if(phoneDigits.length == 10){
        phone = phoneDigits;
      }
      else{
        phone = '';
      }
    }
    this.accountFormattedAddress = company + this.accountFormattedAddress + (phone != '' ? '<br>' + phone : phone);
  }

  private mapAddress(address: LabDetails): Address {
    const a = new Address();

    a.city = address.city;
    a.country = address.country;
    a.line1 = address.streetNumberAndStreet;
    a.state = address.state;
    a.zipCode = address.zipCode;
    a.isoCode = address.country;
    return a;
  }

  private getGrandTotal(shippingPrice: number, orderPrice: number): number {
    const shippingService = this.currentOrderService.getShippingService();
    return shippingService
      ? this.currentOrderService.currentOrder.displayPrice ? orderPrice + shippingPrice : shippingPrice
      : orderPrice;
  }
}
